/*
 * Schuh Galaxy: product page behaviours.
 * - [data-novo-refresh="<key>"] elements (stock dot, discount badge, button price) are swapped with the
 *   matching element from the section HTML Horizon fetches when the variant changes.
 * - <novo-pdp-recs> loads Shopify product recommendations into the product rail.
 * Loaded once from layout/theme.liquid; custom elements survive theme-editor re-renders.
 */

/** Smooth scrolling, unless the visitor asked for reduced motion. */
const novoScrollBehavior = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

(async () => {
  let productSelect = 'shopify:product:select';
  try {
    const { StandardEvents } = await import('@shopify/events');
    if (StandardEvents?.productSelect) productSelect = StandardEvents.productSelect;
  } catch (error) {
    // Keep the fallback event name.
  }

  document.addEventListener(productSelect, (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('product-card') || !event.promise) return;
    const scope = target.closest('.shopify-section, dialog') || document;

    event.promise
      .then(({ detail } = {}) => {
        const html = detail?.html;
        if (!html) return;
        scope.querySelectorAll('[data-novo-refresh]').forEach((element) => {
          const key = element.getAttribute('data-novo-refresh');
          const fresh = html.querySelector(`[data-novo-refresh="${key}"]`);
          if (fresh && fresh.innerHTML !== element.innerHTML) element.innerHTML = fresh.innerHTML;
        });
        // Softstead volume offer: keep the chosen deal and the discounted button price after the swap.
        setTimeout(() => {
          document.querySelectorAll('novo-bundle').forEach((b) => b.refresh?.());
          ssGalleryMore();
        }, 0);
      })
      .catch(() => {});
  });
})();

/**
 * Product recommendations rail: fetch the section as soon as the page is idle after load, so it is ready before the
 * shopper scrolls down (Dennis, 2026-11-01: it sometimes appeared late). The viewport observer stays as a fallback; its
 * rootMargin alone did not preload on desktop, where Horizon scrolls inside .page-wrapper instead of the window.
 */
class NovoPdpRecs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded || this.querySelector('.novo-rail__track')) return;
    const idleLoad = () => requestIdleCallback(() => this.dataset.loaded || this.load(), { timeout: 1500 });
    if (document.readyState === 'complete') idleLoad();
    else window.addEventListener('load', idleLoad, { once: true });
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        if (!this.dataset.loaded) this.load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    this.dataset.loaded = 'true';
    try {
      const response = await fetch(this.dataset.url || '');
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('novo-pdp-recs');
      if (fresh && fresh.innerHTML.trim()) {
        this.innerHTML = fresh.innerHTML;
        this.observer?.disconnect();
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
        }
      } else {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    } catch (error) {
      // Recommendations are optional; leave the section empty.
    }
  }
}

if (!customElements.get('novo-pdp-recs')) customElements.define('novo-pdp-recs', NovoPdpRecs);

/* Photoshoot gallery: show the photo of the chosen colour; dots follow the mobile slider. */
class NovoShootGallery extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.product-information, .quick-add-modal__content, product-component') || document;
    this.onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const fieldset = input.closest('fieldset');
      if (!fieldset || !fieldset.classList.contains('variant-option--swatches')) return;
      this.showColor(input.value);
    };
    this.scope.addEventListener('change', this.onChange);
    // Mobile thumbnail row: tap scrolls the track, swiping marks the thumbnail of the photo in view.
    this.track = this.querySelector('[data-novo-shoot-track]');
    this.onThumb = (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('.novo-shoot__thumb') : null;
      if (!thumb || !this.track) return;
      const i = this.visibleThumbs().indexOf(thumb);
      if (i >= 0) this.track.scrollTo({ left: i * this.track.clientWidth, behavior: novoScrollBehavior() });
    };
    this.addEventListener('click', this.onThumb);
    this.track?.addEventListener('scroll', () => this.markThumb(), { passive: true });
  }

  visibleThumbs() {
    return [...this.querySelectorAll('.novo-shoot__thumb')].filter(
      (t) => !t.classList.contains('novo-shoot__thumb--color') || t.classList.contains('is-current')
    );
  }

  markThumb() {
    if (!this.track) return;
    const i = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
    this.visibleThumbs().forEach((t, k) => t.setAttribute('aria-selected', String(k === i)));
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('change', this.onChange);
  }

  showColor(color) {
    const items = [...this.querySelectorAll('.novo-shoot__item--color')];
    if (!items.some((item) => item.dataset.color === color)) return;
    items.forEach((item) => item.classList.toggle('is-current', item.dataset.color === color));
    this.querySelectorAll('.novo-shoot__thumb--color').forEach((t) => t.classList.toggle('is-current', t.dataset.color === color));
    this.dataset.color = color;
    if (this.track) this.track.scrollTo({ left: 0 });
    this.markThumb();
  }
}

if (!customElements.get('novo-shoot-gallery')) customElements.define('novo-shoot-gallery', NovoShootGallery);

/* Review line: one real review at a time, rotating; pauses on hover/focus and for reduced motion. */
class NovoReviewMini extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.novo-review-mini__item')];
    if (!this.items.length) return;
    this.i = 0;
    this.items[0].classList.add('is-active');
    this.classList.add('is-ready');
    if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ms = Number(this.dataset.interval) || 5000;
    const tick = () => {
      this.items[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.items.length;
      this.items[this.i].classList.add('is-active');
    };
    this.start = () => { clearInterval(this.timer); this.timer = setInterval(tick, ms); };
    this.stop = () => clearInterval(this.timer);
    this.addEventListener('mouseenter', this.stop);
    this.addEventListener('mouseleave', this.start);
    this.start();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }
}

if (!customElements.get('novo-review-mini')) customElements.define('novo-review-mini', NovoReviewMini);

/** Softstead review slider above "Complete the room" (blocks/ss-pdp-review-slider.liquid): arrows wrap around, dots follow the swipe. */
class SsReviewSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ss-review-slider__track');
    this.dots = [...this.querySelectorAll('.ss-review-slider__dot')];
    if (!this.track) return;
    // Safari can restore a scrolled position on load; always start at the first review.
    this.track.scrollLeft = 0;
    this.querySelectorAll('[data-dir]').forEach((button) => {
      button.addEventListener('click', () => this.go(this.index() + Number(button.dataset.dir)));
    });
    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.paint());
      },
      { passive: true }
    );
  }

  count() {
    return this.track.children.length;
  }

  index() {
    return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
  }

  go(i) {
    const n = this.count();
    const next = ((i % n) + n) % n;
    this.track.scrollTo({ left: next * this.track.clientWidth, behavior: novoScrollBehavior() });
  }

  paint() {
    const i = this.index();
    this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
  }
}

if (!customElements.get('ss-review-slider')) customElements.define('ss-review-slider', SsReviewSlider);

/* Product reviews on mobile: show more / show less after the first four (sections/novo-pdp-reviews.liquid). */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-novo-reviews-toggle]') : null;
  if (!button) return;
  const section = button.closest('.novo-pdp-reviews');
  const open = section?.classList.toggle('is-expanded') ?? false;
  button.setAttribute('aria-expanded', String(open));
  const label = button.querySelector('[data-novo-reviews-label]') || button;
  label.textContent = open ? button.dataset.less || '' : button.dataset.more || '';
  if (!open) section?.scrollIntoView({ block: 'start', behavior: novoScrollBehavior() });
});

/* ------------------------------------------------------------------------------------------------
 * Softstead: question chips open the matching FAQ answer (sections/novo-pdp-faq.liquid, id="faq-<handle>").
 * ---------------------------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const chip = event.target instanceof Element ? event.target.closest('a[data-faq-chip], a.novo-pdp-chips__chip') : null;
  if (!chip) return;
  const id = decodeURIComponent((chip.getAttribute('href') || '').replace(/^#/, ''));
  const target = id ? document.getElementById(id) : null;
  const item = target || document.getElementById('novo-faq');
  if (!item) return;
  event.preventDefault();
  if (item instanceof HTMLDetailsElement) item.open = true;
  item.scrollIntoView({ block: 'center', behavior: novoScrollBehavior() });
  item.querySelector('summary')?.focus({ preventScroll: true });
});

/* ------------------------------------------------------------------------------------------------
 * Softstead volume offer (blocks/novo-pdp-bundle.liquid, metafield custom.bundle_tiers).
 * The maths is identical to the checkout Shopify Function (bundle-app/.../cart_lines_discounts_generate_run.js)
 * and to the live Softstead theme: per unit, floored; threshold rules = best percentage reached on every
 * unit; set rules = largest sets first, biggest rewards on the cheapest units; the better of the two wins.
 * A deal tap sets the quantity of the product form; "Choose your colours" opens the mix and match sheet.
 * ---------------------------------------------------------------------------------------------- */
function novoBundleSavingUnits(tiers, units) {
  const q = units.length;
  let pct = 0;
  tiers.forEach((t) => { if (!t.k && q >= t.min && t.off > pct) pct = t.off; });
  const a = units.reduce((sum, c) => sum + Math.floor((c * pct) / 100), 0);
  let rem = q;
  const rewards = [];
  tiers.filter((t) => t.k > 0).sort((x, y) => y.min - x.min).forEach((t) => {
    const n = Math.floor(rem / t.min);
    rem -= n * t.min;
    for (let i = 0; i < n * t.k; i++) rewards.push(t.off);
  });
  rewards.sort((x, y) => y - x);
  let b = 0;
  units.slice().sort((x, y) => x - y).forEach((c, i) => { if (i < rewards.length) b += Math.floor((c * rewards[i]) / 100); });
  return Math.max(a, b);
}

function novoFormatMoney(cents, format) {
  const fmt = format || '£{{amount}}';
  const value = (Number(cents) || 0) / 100;
  const withDelims = (n, decimals, thousands, decimal) => {
    const fixed = n.toFixed(decimals).split('.');
    fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fixed.join(decimal);
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
      default: return withDelims(value, 2, ',', '.');
    }
  });
}

const novoEsc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Chosen quantity per product, so a variant change (Horizon re-renders the section) keeps the deal. */
const novoBundleQty = new Map();

class NovoBundle extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('[data-bundle-data]')?.textContent || '{}');
    } catch (error) {
      this.data = {};
    }
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (error) {
      this.tiers = [];
    }
    this.variants = this.data.variants || [];
    this.tierButtons = [...this.querySelectorAll('[data-tier]')];
    this.pickButton = this.querySelector('[data-bundle-pick]');
    this.onClick = (event) => {
      const tier = event.target instanceof Element ? event.target.closest('[data-tier]') : null;
      if (tier) {
        let qty = Number(tier.dataset.min) || 1;
        // Without a "1 item" option, tapping the chosen deal again goes back to a single item.
        if (tier.classList.contains('is-active') && !this.querySelector('[data-tier][data-min="1"]')) qty = 1;
        novoBundleQty.set(this.dataset.productId, qty);
        this.setQty(qty);
        this.paint();
        return;
      }
      if (event.target instanceof Element && event.target.closest('[data-bundle-pick]')) {
        this.openSheet(Math.max(this.qty(), 2), this.pickButton);
      }
    };
    this.addEventListener('click', this.onClick);
    this.onInput = () => this.paint();
    this.input()?.addEventListener('change', this.onInput);
    const saved = novoBundleQty.get(this.dataset.productId);
    if (saved) this.setQty(saved);
    this.paint();
    this.openEditFromUrl();
  }

  /**
   * "Edit" on a cart line links here with ?edit_set=1: open the sheet in edit mode, prefilled with this product's
   * items from the cart (drawSheet reads /cart.js). The parameter is removed right away, so a refresh or a later
   * re-render of the section does not open it again. Closing without saving leaves the cart as it was.
   */
  openEditFromUrl() {
    if (NovoBundle.editHandled || !this.hasAttribute('data-picker') || this.closest('.quick-add-modal')) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit_set') !== '1') return;
    NovoBundle.editHandled = true;
    params.delete('edit_set');
    const query = params.toString();
    history.replaceState(history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    requestAnimationFrame(() => this.openSheet(2, this.pickButton));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  form() {
    return document.getElementById(this.dataset.form || '');
  }

  input() {
    return this.form()?.querySelector('input[name="quantity"]') || null;
  }

  qty() {
    return Math.max(1, parseInt(this.input()?.value || '1', 10) || 1);
  }

  setQty(n) {
    const input = this.input();
    if (!input) return;
    const max = parseInt(input.max, 10) || 99;
    input.value = String(Math.min(max, n));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  currentVariant() {
    const id = Number(this.form()?.querySelector('input[name="id"]')?.value);
    return this.variants.find((v) => v.id === id) || this.variants[0];
  }

  /** Re-apply the chosen deal after Horizon swapped the section for another variant. */
  refresh() {
    const saved = novoBundleQty.get(this.dataset.productId);
    if (saved && saved !== this.qty()) this.setQty(saved);
    this.paint();
  }

  paint() {
    const qty = this.qty();
    const variant = this.currentVariant();
    let best = null;
    this.tierButtons.forEach((b) => {
      const m = Number(b.dataset.min) || 0;
      if (qty >= m && (!best || m > Number(best.dataset.min))) best = b;
    });
    this.tierButtons.forEach((b) => {
      b.classList.toggle('is-active', b === best);
      b.setAttribute('aria-pressed', String(b === best));
      // An amount follows the variant price; a [percent] label stays the same (set rules keep their wording).
      const min = Number(b.dataset.min) || 1;
      const save = b.querySelector('.novo-pdp-bundle__save');
      if (!variant || !save || min < 2 || Number(b.dataset.k) || !Number(b.dataset.off)) return;
      const units = Array(min).fill(variant.price);
      const text = (this.dataset.labelSave || 'Save [amount]')
        .replace('[percent]', `${Number(b.dataset.off)}%`)
        .replace('[amount]', novoFormatMoney(novoBundleSavingUnits(this.tiers, units), this.data.money));
      save.textContent = text;
      b.dataset.value = text;
    });
    if (this.pickButton) this.pickButton.hidden = !(this.hasAttribute('data-picker') && qty > 1);
    this.paintButton(qty, variant);
  }

  /** Price inside the add-to-cart button: total for the chosen quantity, after the volume discount. */
  paintButton(qty, variant) {
    const price = this.form()?.querySelector('.novo-atc-price');
    if (!price || !variant) return;
    const units = Array(qty).fill(variant.price);
    const original = variant.price * qty;
    const save = novoBundleSavingUnits(this.tiers, units);
    const money = (c) => novoEsc(novoFormatMoney(c, this.data.money));
    // Only the price the customer pays (Dennis, 2026-10-26): no struck-through original in the button.
    price.innerHTML = '<span aria-hidden="true"> · </span>' + money(original - save);
  }

  /* ---------- mix and match sheet: one row per item, each with its own variant ---------- */
  T(key, fallback) {
    return this.getAttribute(`data-picker-${key}`) || fallback;
  }

  openSheet(count, opener) {
    this.opener = opener;
    const sheet = this.buildSheet();
    sheet.querySelector('[data-bp-title]').textContent = this.T('title', 'Build your set');
    sheet.querySelector('[data-bp-body]').innerHTML = '<p class="novo-bp__loading">…</p>';
    sheet.querySelector('[data-bp-foot]').innerHTML = '';
    if (typeof sheet.showModal === 'function' && !sheet.open) sheet.showModal();
    let done = false;
    const go = (cart) => {
      if (done) return;
      done = true;
      this.drawSheet(count, cart);
    };
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(go)
      .catch(() => go(null));
    setTimeout(() => go(null), 1500);
  }

  buildSheet() {
    let sheet = document.getElementById('novo-bundle-sheet');
    if (sheet) return sheet;
    sheet = document.createElement('dialog');
    sheet.id = 'novo-bundle-sheet';
    sheet.className = 'novo-bp';
    sheet.setAttribute('aria-labelledby', 'novo-bp-title');
    sheet.innerHTML =
      '<div class="novo-bp__panel">' +
      '<div class="novo-bp__head"><p class="novo-bp__title" id="novo-bp-title" data-bp-title></p>' +
      `<button type="button" class="novo-bp__close" data-bp-close aria-label="${novoEsc(this.T('close', 'Close'))}">` +
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>' +
      '</button></div>' +
      '<div class="novo-bp__body" data-bp-body></div>' +
      '<div class="novo-bp__foot" data-bp-foot></div>' +
      '</div>';
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close();
    });
    // Softstead: every close (button, backdrop, Esc, after adding) slides the panel out and fades the backdrop
    // before the dialog really closes. Reduced motion: closes at once.
    const nativeClose = HTMLDialogElement.prototype.close.bind(sheet);
    sheet.close = () => {
      if (!sheet.open || sheet.classList.contains('is-closing')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        nativeClose();
        return;
      }
      sheet.classList.add('is-closing');
      const panel = sheet.querySelector('.novo-bp__panel');
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        sheet.classList.remove('is-closing');
        nativeClose();
      };
      panel?.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 400);
    };
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      sheet.close();
    });
    document.body.appendChild(sheet);
    return sheet;
  }

  drawSheet(count, cart) {
    const sheet = this.buildSheet();
    const names = this.data.options || [];
    const values = this.data.values || [];
    const realIdx = names.map((_, i) => i).filter((i) => !(values[i]?.length === 1 && values[i][0] === 'Default Title'));
    const colourIdx = names.findIndex((n) => /^(colour|color|farbe)$/i.test(String(n || '').trim()));
    const match = (o) => this.variants.find((v) => v.o.every((x, i) => x === o[i])) || null;
    const input = this.input();
    const MAX = Math.min(12, parseInt(input?.max, 10) || 99);
    const firstOk = this.variants.find((v) => v.available) || this.variants[0];
    const cur = this.currentVariant();
    const base = (cur && cur.available ? cur : firstOk).o.slice();
    const productId = String(this.dataset.productId);

    const inCart = [];
    const cartIds = [];
    ((cart && cart.items) || []).forEach((it) => {
      if (String(it.product_id) !== productId) return;
      // Engraved or otherwise personalised lines are left alone.
      if (it.properties && Object.keys(it.properties).some((k) => !k.startsWith('_'))) return;
      const v = this.variants.find((x) => x.id === it.variant_id);
      if (!v) return;
      cartIds.push(v.id);
      for (let n = 0; n < it.quantity; n++) inCart.push(v.o.slice());
    });
    const editing = inCart.length > 0;
    let slots = (this.saved || inCart).filter((o) => match(o)?.available).slice(0, MAX);
    const want = Math.min(MAX, Math.max(1, slots.length && editing && count <= slots.length ? slots.length : count));
    while (slots.length < want) slots.push((slots[slots.length - 1] || base).slice());
    slots.length = want;
    this.saved = slots;
    let openIdx = 0;
    const money = (c) => novoEsc(novoFormatMoney(c, this.data.money));
    const body = sheet.querySelector('[data-bp-body]');
    const foot = sheet.querySelector('[data-bp-foot]');

    const colourImage = (val) => this.variants.find((v) => v.o[colourIdx] === val && v.img)?.img || '';

    const draw = () => {
      let html = '';
      slots.forEach((o, n) => {
        const v = match(o);
        const isOpen = n === openIdx;
        const summary = realIdx.map((i) => o[i]).filter(Boolean).join(' · ');
        html +=
          `<section class="novo-bp__item${isOpen ? ' is-open' : ''}">` +
          `<button type="button" class="novo-bp__row" data-bp-toggle="${n}" aria-expanded="${isOpen}">` +
          (v && v.img ? `<img class="novo-bp__thumb" src="${novoEsc(v.img)}" alt="" width="48" height="48" loading="lazy">` : '<span class="novo-bp__thumb"></span>') +
          `<span class="novo-bp__meta"><b>${novoEsc(this.T('item', 'Item [n]').replace('[n]', n + 1))}</b><span>${novoEsc(summary)}</span></span>` +
          `<span class="novo-bp__price">${v ? money(v.price) : ''}</span>` +
          `<span class="novo-bp__edit">${novoEsc(this.T('edit', 'Change'))}</span>` +
          '</button>';
        if (isOpen) {
          html += '<div class="novo-bp__opts">';
          const order = realIdx.slice();
          if (colourIdx > 0 && order.includes(colourIdx)) {
            order.splice(order.indexOf(colourIdx), 1);
            order.unshift(colourIdx);
          }
          order.forEach((i) => {
            if ((values[i] || []).length < 2) return;
            html += `<div class="novo-bp__opt"><p class="novo-bp__optlabel">${novoEsc(names[i])} <span>${novoEsc(o[i])}</span></p><div class="novo-bp__values" role="group" aria-label="${novoEsc(names[i])}">`;
            values[i].forEach((val) => {
              const t = o.slice();
              t[i] = val;
              const cand = match(t);
              const any = this.variants.some((x) => x.available && x.o[i] === val);
              const disabled = i === colourIdx ? !any : !(cand && cand.available);
              const img = i === colourIdx ? colourImage(val) : '';
              const on = o[i] === val;
              html += img
                ? `<button type="button" class="novo-bp__swatch${on ? ' is-on' : ''}" data-bp-opt="${i}" data-val="${novoEsc(val)}" aria-pressed="${on}" title="${novoEsc(val)}" aria-label="${novoEsc(val)}"${disabled ? ' disabled' : ''}><img src="${novoEsc(img)}" alt="" width="40" height="40" loading="lazy"></button>`
                : `<button type="button" class="novo-bp__pill${on ? ' is-on' : ''}" data-bp-opt="${i}" data-val="${novoEsc(val)}" aria-pressed="${on}"${disabled ? ' disabled' : ''}>${novoEsc(val)}</button>`;
            });
            html += '</div></div>';
          });
          if (slots.length > 1) html += `<button type="button" class="novo-bp__remove" data-bp-remove="${n}">${novoEsc(this.T('remove', 'Remove'))}</button>`;
          html += '</div>';
        }
        html += '</section>';
      });
      body.innerHTML = html;

      const units = [];
      let ok = true;
      slots.forEach((o) => {
        const v = match(o);
        if (v && v.available) units.push(v.price);
        else ok = false;
      });
      const original = units.reduce((a, c) => a + c, 0);
      const save = novoBundleSavingUnits(this.tiers, units);
      let next = null;
      this.tierButtons.forEach((b) => {
        const m = Number(b.dataset.min) || 0;
        if (m > slots.length && m <= MAX && (!next || m < next.m)) next = { m, text: b.dataset.value || '' };
      });
      foot.innerHTML =
        (next
          ? `<button type="button" class="novo-bp__more" data-bp-more="${next.m - slots.length}"><span aria-hidden="true">+</span> ${novoEsc(this.T('more', 'Add [n] more:').replace('[n]', next.m - slots.length))} <b>${novoEsc(next.text)}</b></button>`
          : '') +
        '<div class="novo-bp__sum">' +
        (save ? `<span class="novo-bp__save">${novoEsc(this.T('save', 'You save [amount]').replace('[amount]', novoFormatMoney(save, this.data.money)))}</span>` : '<span></span>') +
        `<span class="novo-bp__total">${save ? `<s>${money(original)}</s> ` : ''}<b>${money(original - save)}</b></span>` +
        '</div>' +
        `<button type="button" class="novo-bp__add" data-bp-add${ok ? '' : ' disabled'}>${novoEsc(editing ? this.T('update', 'Update cart') : this.T('add', 'Add [n] to cart').replace('[n]', slots.length))}</button>`;
    };

    sheet.onclick = (event) => {
      const el = event.target instanceof Element ? event.target : null;
      if (!el) return;
      let t;
      if (el === sheet || el.closest('[data-bp-close]')) {
        sheet.close();
        return;
      }
      if ((t = el.closest('[data-bp-toggle]'))) {
        const n = Number(t.getAttribute('data-bp-toggle'));
        openIdx = openIdx === n ? -1 : n;
        draw();
        return;
      }
      if ((t = el.closest('[data-bp-opt]')) && !t.disabled) {
        const i = Number(t.getAttribute('data-bp-opt'));
        const o = slots[openIdx];
        o[i] = t.getAttribute('data-val');
        if (!match(o) || !match(o).available) {
          const alt = this.variants.find((v) => v.available && v.o[i] === o[i]);
          if (alt) slots[openIdx] = alt.o.slice();
        }
        draw();
        return;
      }
      if ((t = el.closest('[data-bp-remove]'))) {
        slots.splice(Number(t.getAttribute('data-bp-remove')), 1);
        openIdx = Math.min(openIdx, slots.length - 1);
        draw();
        return;
      }
      if ((t = el.closest('[data-bp-more]'))) {
        const add = Number(t.getAttribute('data-bp-more'));
        for (let k = 0; k < add && slots.length < MAX; k++) slots.push(slots[slots.length - 1].slice());
        openIdx = slots.length - add;
        draw();
        return;
      }
      if ((t = el.closest('[data-bp-add]')) && !t.disabled) {
        const byId = {};
        slots.forEach((o) => {
          const v = match(o);
          if (v) byId[v.id] = (byId[v.id] || 0) + 1;
        });
        t.disabled = true;
        t.classList.add('is-loading');
        this.submitSheet(byId, editing ? cartIds : null)
          .then(() => {
            this.saved = null;
            novoBundleQty.delete(this.dataset.productId);
            this.setQty(1);
            this.paint();
            // Horizon only auto-opens the drawer after an add; after an edit, show the updated cart once the sheet is gone.
            if (editing) {
              sheet.addEventListener(
                'close',
                () => {
                  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
                  if (drawer && !drawer.isOpen) drawer.open();
                },
                { once: true }
              );
            }
            sheet.close();
          })
          .catch(() => {
            t.disabled = false;
            t.classList.remove('is-loading');
          });
      }
    };

    draw();
    sheet.querySelector('[data-bp-title]').textContent = this.T('title', 'Build your set');
  }

  /** Adds (or, when editing, replaces) the lines, then lets Horizon's cart drawer update and open. */
  async submitSheet(byId, replaceIds) {
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
    let CartLinesUpdateEvent = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    } catch (error) {
      CartLinesUpdateEvent = null;
    }
    const lines = Object.keys(byId).map((id) => ({ merchandiseId: id, quantity: byId[id] }));
    const deferred = CartLinesUpdateEvent?.createPromise ? CartLinesUpdateEvent.createPromise() : null;
    const source = this.form()?.closest('product-form-component') || this;
    if (CartLinesUpdateEvent && deferred) {
      source.dispatchEvent(new CartLinesUpdateEvent({ action: replaceIds ? 'update' : 'add', context: 'product', lines, promise: deferred.promise }));
    }
    try {
      let response;
      if (replaceIds) {
        const updates = {};
        replaceIds.forEach((id) => { updates[id] = 0; });
        Object.keys(byId).forEach((id) => { updates[id] = byId[id]; });
        response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: sectionIds }),
        }).then((r) => r.json());
      } else {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: Object.keys(byId).map((id) => ({ id: Number(id), quantity: byId[id] })), sections: sectionIds }),
        }).then((r) => r.json());
      }
      if (response && response.status) throw new Error(response.description || response.message || 'Cart error');
      const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : cart,
        detail: {
          items: cart.items,
          source: 'novo-bundle',
          sourceId: 'novo-bundle',
          itemCount: lines.reduce((a, l) => a + l.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      return cart;
    } catch (error) {
      deferred?.reject(error);
      throw error;
    }
  }
}

if (!customElements.get('novo-bundle')) customElements.define('novo-bundle', NovoBundle);

/* ------------------------------------------------------------------------------------------------
 * Softstead: product galleries with many photos (some have 27) show the first 6 in the desktop grid,
 * the rest behind one button. Re-checked after Horizon re-renders the gallery for another variant.
 * ---------------------------------------------------------------------------------------------- */
function ssGalleryMore() {
  if (document.documentElement.hasAttribute('data-ss-gallery-open')) return;
  document.querySelectorAll('.product-information .media-gallery__grid').forEach((grid) => {
    const count = grid.querySelectorAll(':scope > li').length;
    const next = grid.nextElementSibling;
    if (count <= 6) {
      if (next?.classList.contains('ss-gallery-more')) next.remove();
      return;
    }
    const label = 'Show all images';
    if (next?.classList.contains('ss-gallery-more')) {
      next.querySelector('button').textContent = label;
      return;
    }
    const wrap = document.createElement('div');
    wrap.className = 'ss-gallery-more';
    wrap.innerHTML = `<button type="button">${label}</button>`;
    wrap.querySelector('button').addEventListener('click', () => {
      document.documentElement.setAttribute('data-ss-gallery-open', '');
      wrap.remove();
    });
    grid.after(wrap);
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ssGalleryMore);
else ssGalleryMore();
document.addEventListener('shopify:section:load', ssGalleryMore);

/* ------------------------------------------------------------------------------------------------
 * Softstead: review cards on desktop are as tall as the grey summary block. Cards whose text does not
 * fit get a fade and a "More" button that expands only that card ("Less" collapses it again).
 * ---------------------------------------------------------------------------------------------- */
const ssReviewsDesktop = window.matchMedia('(min-width: 1024px)');

function ssLayoutReviews() {
  document.querySelectorAll('.novo-pdp-reviews').forEach((section) => {
    const summary = section.querySelector('.novo-pdp-reviews__summary');
    const cards = [...section.querySelectorAll('.novo-pdp-reviews__card')];
    if (!summary || !cards.length) return;
    if (!ssReviewsDesktop.matches) {
      // Phones and tablets: text clamped to three lines (CSS), "More" / "Less" only where it overflows.
      section.style.removeProperty('--ss-rev-h');
      cards.forEach((card) => {
        if (card.classList.contains('is-open')) return;
        const text = card.querySelector('.novo-pdp-reviews__text');
        const button = card.querySelector('[data-review-more]');
        if (!text || !button) return;
        const clamped = text.scrollHeight > text.clientHeight + 2;
        button.hidden = !clamped;
        card.classList.toggle('is-clamped', clamped);
      });
      return;
    }
    section.style.setProperty('--ss-rev-h', `${summary.offsetHeight}px`);
    cards.forEach((card) => {
      if (card.classList.contains('is-open')) return;
      const text = card.querySelector('.novo-pdp-reviews__text');
      const button = card.querySelector('[data-review-more]');
      if (!text || !button) return;
      // Measure with the button shown, so the space it takes is part of the check.
      button.hidden = false;
      const clamped = text.scrollHeight > text.clientHeight + 2;
      button.hidden = !clamped;
      card.classList.toggle('is-clamped', clamped);
    });
  });
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-review-more]') : null;
  if (!button) return;
  const card = button.closest('.novo-pdp-reviews__card');
  if (!card) return;
  const open = card.classList.toggle('is-open');
  button.setAttribute('aria-expanded', String(open));
  button.textContent = open ? button.dataset.less || 'Less' : button.dataset.more || 'More';
  if (!open) ssLayoutReviews();
});

let ssReviewsTimer;
const ssReviewsSoon = () => {
  clearTimeout(ssReviewsTimer);
  ssReviewsTimer = setTimeout(ssLayoutReviews, 120);
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ssLayoutReviews);
else ssLayoutReviews();
window.addEventListener('load', ssLayoutReviews);
window.addEventListener('resize', ssReviewsSoon);
document.addEventListener('shopify:section:load', ssLayoutReviews);
document.fonts?.ready.then(ssLayoutReviews);

/* ------------------------------------------------------------------ */
/* Loading feedback (Softstead): variant change                         */
/* Picking a size or colour makes Horizon fetch the new variant (price,  */
/* availability). Until it has re-rendered, the add to cart buttons of   */
/* that product (main, sticky bar, quick add) show a ring.               */
/* ------------------------------------------------------------------ */

const SS_ATC_SELECTOR =
  'add-to-cart-component button.add-to-cart-button, .sticky-add-to-cart__button, add-to-cart-component button[ref="addToCartButton"]';

/** @param {Element} scope */
function ssClearVariantBusy(scope) {
  scope.querySelectorAll('.novo-variant-busy').forEach((button) => {
    button.classList.remove('novo-variant-busy');
    button.removeAttribute('aria-busy');
    button.querySelector(':scope > .novo-busy')?.remove();
  });
}

document.addEventListener(
  'shopify:product:select',
  (event) => {
    const picker = event.target instanceof Element ? event.target : null;
    if (!picker) return;
    const scope = picker.closest('.quick-add-modal__content, .shopify-section') || document.body;
    const token = {};
    /** @type {any} */ (scope).ssVariantToken = token;
    scope.querySelectorAll(SS_ATC_SELECTOR).forEach((button) => {
      if (!(button instanceof HTMLElement) || button.classList.contains('novo-variant-busy')) return;
      // Buttons of other products (upsell, recommendation cards) are not affected by this choice.
      if (button.closest('product-card, quick-add-component, .novo-pdp-upsell, .novo-card')) return;
      // Light ring on a dark button, dark ring on a light one.
      const rgb = (getComputedStyle(button).backgroundColor.match(/[\d.]+/g) || []).map(Number);
      const dark = rgb.length >= 3 && (rgb[3] ?? 1) > 0.5 && 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] < 128;
      button.classList.add('novo-variant-busy');
      button.setAttribute('aria-busy', 'true');
      button.insertAdjacentHTML('beforeend', `<span class="novo-busy${dark ? ' novo-busy--inverse' : ''}" aria-hidden="true"></span>`);
    });
    const done = () => {
      // A newer selection owns the buttons now; it clears them when its own fetch is done.
      if (/** @type {any} */ (scope).ssVariantToken !== token) return;
      // Two frames: Horizon's listeners morph price and buttons right after the promise settles.
      requestAnimationFrame(() => requestAnimationFrame(() => ssClearVariantBusy(scope)));
    };
    const promise = /** @type {any} */ (event).promise;
    if (promise && typeof promise.then === 'function') promise.then(done, done);
    else done();
    // Safety net: never leave the button blocked.
    setTimeout(() => {
      if (/** @type {any} */ (scope).ssVariantToken === token) ssClearVariantBusy(scope);
    }, 8000);
  },
  true
);
